(() => {
  const instances = new Map();
  const modes = ['diameter', 'circumference'];
  // Atasay's Turkish ring-size chart, sizes 6–34 (diameter and circumference in mm).
  // https://92d339.cdn.akinoncloud.com/cms/2024/03/11/6926147f-6cd2-4faa-a425-d533d37a7f27.pdf
  const sizeChart = [14.7, 15, 15.3, 15.6, 15.9, 16.2, 16.5, 16.8, 17.2, 17.5,
    17.8, 18.1, 18.4, 18.8, 19.1, 19.4, 19.7, 20, 20.3, 20.6, 21, 21.3,
    21.6, 22, 22.3, 22.6, 22.9, 23.2, 23.5].map((diameter, index) => ({
    diameter, circumference: index + 46, size: index + 6
  }));
  let nextId = 0;

  function bind(root) {
    if (instances.has(root) || root.dataset.ringReady === 'true') return;
    const tabs = modes.map(mode => root.querySelector(`[data-ring-method="${mode}"]`));
    const panels = modes.map(mode => root.querySelector(`[data-ring-panel="${mode}"]`));
    const notebook = root.querySelector('[data-ring-notebook]');
    const input = root.querySelector('[data-ring-input]');
    const label = root.querySelector('[data-ring-label]');
    const output = root.querySelector('[data-ring-output]');
    const error = root.querySelector('[data-ring-error]');
    const copy = root.querySelector('[data-ring-copy]');
    if ([...tabs, ...panels, notebook, input, label, output, error, copy].some(element => !element)) return;
    const tablist = tabs[0].closest('[data-ring-methods]') || tabs[0].parentElement;
    if (!tablist || !tabs.every(tab => tablist.contains(tab))) return;

    const controller = new AbortController();
    const listen = (element, event, callback) => element.addEventListener(event, callback, { signal: controller.signal });
    const savedAttributes = [];
    const setAttribute = (element, name, value) => {
      savedAttributes.push([element, name, element.getAttribute(name)]);
      element.setAttribute(name, value);
    };
    const identifier = `${root.id || 'mh-ring-guide'}-${++nextId}`;
    const locale = /^tr(?:-|$)/i.test(document.documentElement.lang) ? 'tr-TR' : 'en-US';
    const numberFormat = new Intl.NumberFormat(locale, { useGrouping: false, maximumFractionDigits: 2 });
    let mode = 'diameter';
    let touched = false;
    let result = '';
    let copyAttempt = 0;
    let destroyed = false;

    function textFor(name) {
      return root.dataset[`${name}${mode === 'diameter' ? 'Diameter' : 'Circumference'}`] || '';
    }

    function resetCopy() {
      copyAttempt += 1;
      copy.textContent = root.dataset.copyLabel || '';
    }

    function render(showError = touched) {
      const raw = input.value.trim();
      const parsed = /^\d+(?:[.,]\d{1,2})?$/.test(raw) ? Number(raw.replace(',', '.')) : NaN;
      const minimum = sizeChart[0][mode];
      const maximum = sizeChart[sizeChart.length - 1][mode];
      const valid = Number.isFinite(parsed) && parsed >= minimum && parsed <= maximum;
      result = '';
      output.replaceChildren();
      if (valid) {
        // Prefer the larger size at an exact midpoint, allowing for decimal precision.
        const nearest = sizeChart.reduce((best, row) =>
          Math.abs(row[mode] - parsed) <= Math.abs(best[mode] - parsed) + 1e-9 ? row : best);
        const measurement = `${textFor('prefix')}: ${numberFormat.format(parsed)} mm`;
        result = `TR ${nearest.size} (${measurement})`;
        [
          ['span', 'result-label', root.dataset.resultTitle || ''],
          ['strong', 'result-size', `TR ${nearest.size}`],
          ['span', 'result-measurement', measurement],
          ['span', 'result-note', root.dataset.roundingHint || '']
        ].forEach(([tag, className, content]) => {
          const element = document.createElement(tag);
          element.className = `mh-ring-guide__${className}`;
          element.textContent = content;
          output.append(element);
        });
      }
      copy.disabled = !valid;
      const invalid = !valid && showError;
      input.setAttribute('aria-invalid', String(invalid));
      error.textContent = invalid ? textFor('invalid') : '';
      error.hidden = !invalid;
      return valid;
    }

    function activate(nextMode, focus = false) {
      if (!modes.includes(nextMode)) return;
      const changed = mode !== nextMode;
      mode = nextMode;
      tabs.forEach((tab, index) => {
        const selected = modes[index] === mode;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        tab.classList.toggle('is-active', selected);
        panels[index].hidden = !selected;
        panels[index].classList.toggle('is-active', selected);
      });
      root.dataset.ringMode = mode;
      notebook.dataset.ringMode = mode;
      label.textContent = textFor('label');
      if (changed) {
        input.value = '';
        touched = false;
        resetCopy();
        render(false);
      }
      if (focus) tabs[modes.indexOf(mode)].focus({ preventScroll: true });
    }

    function hashMode() {
      let hash;
      try {
        hash = decodeURIComponent(window.location.hash.slice(1));
      } catch {
        return null;
      }
      const index = panels.findIndex((panel, position) => hash === panel.id || hash === `mh-ring-${modes[position]}`);
      return index < 0 ? null : modes[index];
    }

    function showCopyFallback() {
      error.textContent = root.dataset.copyFallback || '';
      error.hidden = !error.textContent;
      output.textContent = result;
      output.focus({ preventScroll: true });
      const selection = window.getSelection();
      if (!selection) return;
      const range = document.createRange();
      range.selectNodeContents(output);
      selection.removeAllRanges();
      selection.addRange(range);
    }

    setAttribute(tablist, 'role', 'tablist');
    setAttribute(tablist, 'aria-orientation', 'horizontal');
    tabs.forEach((tab, index) => {
      const panel = panels[index];
      if (!tab.id) setAttribute(tab, 'id', `${identifier}-tab-${modes[index]}`);
      if (!panel.id) setAttribute(panel, 'id', `${identifier}-panel-${modes[index]}`);
      setAttribute(tab, 'role', 'tab');
      setAttribute(tab, 'aria-controls', panel.id);
      setAttribute(tab, 'aria-selected', 'false');
      setAttribute(tab, 'tabindex', '-1');
      setAttribute(panel, 'role', 'tabpanel');
      setAttribute(panel, 'aria-labelledby', tab.id);
      setAttribute(panel, 'tabindex', '0');
      listen(tab, 'click', event => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        activate(modes[index]);
      });
      listen(tab, 'keydown', event => {
        if (event.altKey || event.ctrlKey || event.metaKey) return;
        let target;
        if (event.key === 'ArrowRight') target = (index + 1) % tabs.length;
        else if (event.key === 'ArrowLeft') target = (index - 1 + tabs.length) % tabs.length;
        else if (event.key === 'Home') target = 0;
        else if (event.key === 'End') target = tabs.length - 1;
        else if (event.key === ' ') target = index;
        if (target === undefined) return;
        event.preventDefault();
        activate(modes[target], true);
      });
    });
    setAttribute(input, 'inputmode', 'decimal');
    setAttribute(input, 'aria-invalid', 'false');
    setAttribute(output, 'tabindex', '0');

    listen(input, 'input', () => {
      resetCopy();
      render();
    });
    listen(input, 'blur', () => {
      touched = true;
      render(true);
    });
    listen(notebook, 'submit', event => {
      event.preventDefault();
      touched = true;
      render(true);
    });
    listen(copy, 'click', async () => {
      if (copy.disabled || !result) return;
      const value = result;
      const attempt = ++copyAttempt;
      copy.disabled = true;
      error.textContent = '';
      error.hidden = true;
      try {
        if (!navigator.clipboard?.writeText) throw new Error();
        await navigator.clipboard.writeText(value);
        if (destroyed || attempt !== copyAttempt || value !== result) return;
        copy.textContent = root.dataset.copiedLabel || '';
      } catch {
        if (destroyed || attempt !== copyAttempt || value !== result) return;
        showCopyFallback();
      } finally {
        if (!destroyed && attempt === copyAttempt) copy.disabled = !result;
      }
    });
    listen(window, 'hashchange', () => {
      const nextMode = hashMode();
      if (nextMode) activate(nextMode);
    });

    const destroy = () => {
      destroyed = true;
      copyAttempt += 1;
      controller.abort();
      [...savedAttributes].reverse().forEach(([element, name, value]) => {
        if (value === null) element.removeAttribute(name);
        else element.setAttribute(name, value);
      });
      tabs.forEach(tab => tab.classList.remove('is-active'));
      panels.forEach(panel => {
        panel.hidden = false;
        panel.classList.remove('is-active');
      });
      notebook.hidden = true;
      delete notebook.dataset.ringMode;
      delete root.dataset.ringMode;
      delete root.dataset.ringReady;
      instances.delete(root);
    };
    instances.set(root, { destroy });
    input.value = '';
    resetCopy();
    activate(hashMode() || 'diameter');
    render(false);
    notebook.hidden = false;
    root.dataset.ringReady = 'true';
  }

  function scan(scope) {
    if (scope instanceof Element && scope.matches('[data-mh-ring-guide]')) bind(scope);
    scope.querySelectorAll?.('[data-mh-ring-guide]').forEach(bind);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  else scan(document);

  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((instance, root) => {
      if (root === event.target || event.target.contains(root)) instance.destroy();
    });
  });
})();
